export type WorkParameters = {
  station: string;
  activeMinutes: number | null;
  passiveMinutes: number | null;
  batchQuantity: number | null;
  instructions: string;
};

export type KitchenTask = {
  id: number;
  name: string;
  kind: 'dish' | 'semi';
  quantity: number;
  unit: string;
  plannedDate: string;
  status: 'Planned' | 'InProgress' | 'Blocked' | 'Review' | 'Completed' | 'Cancelled';
  assignedUserId: string | null;
  assignedUserName: string;
  station: string;
  estimatedMinutes: number | null;
  deadline: string | null;
  notes: string;
  instructions: string;
  completedQuantity: number;
  version: number;
  orderIds: number[];
  historicalOrderIds?: number[];
  kitchenId?: number | null;
  kitchenName?: string | null;
  canExecute?: boolean;
};

export type WorkPreviewItem = {
  kind: 'dish' | 'semi';
  id: number;
  name: string;
  quantity: number;
  unit: string;
  orderIds: number[];
  parameters: WorkParameters;
  missing: string[];
  recipe?: {
    batchQuantity: number | null;
    cookingMinutes: number | null;
    serveMinutes: number | null;
    instructions: string;
    operations: string[];
  };
};

export const kitchenMissingLabels: Record<string, string> = {
  station: 'станція / напрям',
  activeMinutes: 'підтвердження активного часу працівника',
  batchQuantity: 'вихід базової партії',
};

export type KitchenEmployee = {
  id: string;
  name: string;
  employmentId?: number;
  stations?: string[];
  availableMinutes?: number | null;
};

export function recommendKitchenAssignee(task: KitchenTask, employees: KitchenEmployee[], tasks: KitchenTask[]) {
  if (!task.station.trim() || task.estimatedMinutes === null)
    return { employee: null, reason: 'Спочатку вкажіть станцію та активний час роботи.' };
  const station = task.station.trim().toLocaleLowerCase('uk');
  const candidates = employees.map(employee => {
    const load = getKitchenWorkload(tasks.filter(item =>
      item.id !== task.id && item.assignedUserId === employee.id &&
      item.plannedDate.slice(0, 10) === task.plannedDate.slice(0, 10)), true);
    return { employee, load };
  }).filter(({ employee, load }) =>
    employee.stations?.some(value => value.trim().toLocaleLowerCase('uk') === station) &&
    employee.availableMinutes != null && employee.availableMinutes > 0 && load.unknown === 0 &&
    load.minutes + (task.estimatedMinutes ?? 0) <= employee.availableMinutes);
  candidates.sort((a, b) =>
    (a.load.minutes + (task.estimatedMinutes ?? 0)) / (a.employee.availableMinutes ?? 1) -
    (b.load.minutes + (task.estimatedMinutes ?? 0)) / (b.employee.availableMinutes ?? 1) ||
    a.employee.name.localeCompare(b.employee.name, 'uk'));
  const chosen = candidates[0];
  if (!chosen) return { employee: null, reason: 'Немає підтвердженої станції та достатнього доступного часу, або поточні роботи мають невідому тривалість. Оберіть виконавця вручну.' };
  return {
    employee: chosen.employee,
    reason: `${chosen.employee.name}: станція підтверджена; після призначення ${Math.round(chosen.load.minutes + task.estimatedMinutes)} із ${chosen.employee.availableMinutes} хв. Це рекомендація за навантаженням, не перевірка календаря чи обладнання.`,
  };
}

export const workStatusLabels: Record<KitchenTask['status'], string> = {
  Planned: 'Заплановано',
  InProgress: 'В роботі',
  Blocked: 'Проблема',
  Review: 'На перевірці',
  Completed: 'Прийнято',
  Cancelled: 'Скасовано',
};

export function getKitchenWorkload(tasks: KitchenTask[], includeCompleted = false) {
  const active = tasks.filter(task => task.status !== 'Cancelled' && (includeCompleted || task.status !== 'Completed'));
  return {
    count: active.length,
    minutes: active.reduce((sum, task) => sum + (task.estimatedMinutes ?? 0), 0),
    unknown: active.filter(task => task.estimatedMinutes === null).length,
    blocked: active.filter(task => task.status === 'Blocked').length,
  };
}

export function validateWorkParameters(parameters: WorkParameters): string | null {
  if (!parameters.station.trim()) return 'Вкажіть станцію або напрям роботи.';
  if (parameters.batchQuantity === null || !Number.isFinite(parameters.batchQuantity) || parameters.batchQuantity <= 0)
    return 'Базова партія має бути більше нуля.';
  if (parameters.activeMinutes !== null && (!Number.isFinite(parameters.activeMinutes) || parameters.activeMinutes < 0))
    return 'Активний час має бути не менше нуля або невідомим.';
  if (parameters.passiveMinutes !== null && (!Number.isFinite(parameters.passiveMinutes) || parameters.passiveMinutes < 0))
    return 'Час очікування не може бути від’ємним.';
  return null;
}
